<template>
  <div class="">
    <div
      :data-tooltip-target="`tooltip-${image.name}`"
      class="image-preview relative shadow-lg rounded-lg flex flex-col space-y-1 w-full p-2 border border-solid over:border-sky-300 bg-white dark:bg-gray-900 border-white dark:border-gray-700"
      :class="{
        'bg-linear-to-br from-red-300 to-rose-300': invalid,
        'bg-linear-to-br from-red-200 to-rose-200': errorOccurred,
      }"
    >
      <div
        :id="`tooltip-${image.name}`"
        role="tooltip"
        class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
      >
        <span>{{ image.name }} ({{ imageSize }})</span>
        <div class="tooltip-arrow" data-popper-arrow></div>
      </div>

      <div>
        <div
          class="cursor-pointer max-w-max ml-auto text-3xl hover:text-rose-600 dark:text-gray-400"
          :class="{ invisible: featureStore.processing }"
          @click="removeImage"
        >
          <i class="fa-regular fa-circle-xmark"></i>
        </div>
      </div>

      <div v-if="previewStatus?.status === 'finished' && result?.original_url">
        <AppComparisonSlider :first="getImageURL(image)" :second="result?.url" />
      </div>

      <div :class="`${processed && !errorOccurred ? 'hidden' : 'inline-flex'} flex-grow items-center justify-center`">
        <div class="transparent-wrap transition" :style="imageStyle">
          <img
            :class="{ 'opacity-60': !processed && featureStore.processing }"
            :src="imageURL"
            :alt="image.name"
            class=""
            @load="handImageLoad"
          />
        </div>
      </div>

      <div class="text-gray-600 text-center break-all text-sm line-clamp-1 dark:text-gray-300">
        <span>{{ image.name }}</span>
        <span class="ml-1">({{ imageSize }})</span>
      </div>

      <section>
        <div class="flex flex-wrap justify-center items-center">
          <div v-if="featureStore.feature.slug === 'resize-image'" class="flex flex-wrap justify-center items-center">
            <div class="badge badge--dark">
              <span>{{ imageDimensions.width }}</span>
              <span class="mx-1">x</span>
              <span>{{ imageDimensions.height }}</span>
            </div>
            <span class="text-sm mx-1">
              <i class="fa-solid fa-arrow-right"></i>
            </span>
            <div class="badge badge--green">
              <span>{{ resizeWidth }}</span>
              <span class="mx-1">x</span>
              <span>{{ resizeHeight }}</span>
            </div>
          </div>

          <div v-else class="badge badge--lg badge--dark">
            <span>{{ imageDimensions.width }}</span>
            <span class="mx-1">x</span>
            <span>{{ imageDimensions.height }}</span>
          </div>
        </div>

        <div
          v-if="featureStore.feature.slug === 'convert-image'"
          class="mt-2 flex flex-wrap justify-center items-center"
        >
          <span class="badge badge--lg badge--dark">{{ getImageExtension(image) }}</span>
          <span class="text-sm mx-1">
            <i class="fa-solid fa-arrow-right"></i>
          </span>
          <span class="badge badge--lg badge--green">{{
            featureStore.options[featureStore.feature.slug].convertTo
          }}</span>
        </div>
      </section>

      <div class="absolute z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <!-- Pending -->
        <div v-if="!previewStatus && featureStore.processing">
          <img src="@/images/system/pending.gif" alt="Pending" class="h-1/2" />
          <div class="text-center text-lg lg:text-2xl px-3 py-1 bg-black/50 text-white font-medium text-shadow-lg">
            Pending
          </div>
        </div>
        <!-- Uploading -->
        <div v-else-if="previewStatus?.status === 'uploading'">
          <img src="@/images/system/uploading.gif" alt="Uploading" class="h-1/2" />
          <div class="text-center text-lg lg:text-2xl px-3 py-1 bg-amber-500/50 text-white font-medium text-shadow-lg">
            Uploading
          </div>
        </div>
        <!-- Processing -->
        <div v-else-if="previewStatus?.status === 'processing'">
          <img src="@/images/system/processing.gif" alt="Processing" class="h-1/2" />
          <div class="text-center text-lg lg:text-2xl px-3 py-1 bg-blue-500/50 text-white font-medium text-shadow-lg">
            Processing
          </div>
        </div>
        <!-- Finished -->
        <div v-else-if="previewStatus?.status === 'finished' && !result?.original_url">
          <img src="@/images/system/finished.gif" alt="Finished" class="h-1/2" />
          <div class="text-center text-lg lg:text-2xl px-3 py-1 bg-green-500/50 text-white font-medium text-shadow-lg">
            Finished
          </div>
        </div>
        <!-- Error -->
        <div
          v-else-if="previewStatus?.status === 'error'"
          class="w-full absolute z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-800/80 text-shadow-lg text-red-100 p-4 text-center text-base"
        >
          Sorry, we couldn't process this image. Please try again later.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, watch, computed } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useFeatureStore } from '$/stores/feature'
  import { getImageURL, getImageExtension, formatBytes } from '$/utils'
  import { useFlowbite } from '$/composables/useFlowbite'
  import AppComparisonSlider from '&/AppComparisonSlider.vue'

  useFlowbite(['tooltips'])

  const featureStore = useFeatureStore()
  const imageURL = ref('')
  const imageDimensions = ref({})
  const imageSize = ref('')
  const imageStyle = ref('')

  const resizeWidth = computed(() => featureStore.imageData.find((i) => i.name === props.image.name)?.resizeWidth)
  const resizeHeight = computed(() => featureStore.imageData.find((i) => i.name === props.image.name)?.resizeHeight)

  const props = withDefaults(
    defineProps<{
      image: object
      imageIndex: number
      invalid?: boolean
    }>(),
    {
      invalid: false,
    },
  )

  const route = useRoute()
  const router = useRouter()
  const processed = computed(() => featureStore.results.map((i) => Number(i.image_index)).includes(props.imageIndex))
  const errorMessage = computed(
    () => featureStore.results.find((i) => Number(i.image_index) === props.imageIndex)?.error,
  )
  const errorOccurred = computed(() => processed.value && errorMessage.value)
  const previewStatus = computed(() =>
    featureStore.previewStatuses.find((i) => Number(i.image_index) === props.imageIndex),
  )
  const result = computed(() => featureStore.results.find((i) => Number(i.image_index) === props.imageIndex))

  watch(
    [() => featureStore.options['rotate-image'].rotateAngle, () => featureStore.options['rotate-image'].flipType],
    ([angle, flipType]) => {
      if (featureStore.feature.slug === 'rotate-image') {
        let scaleX = 1
        let scaleY = 1

        if (flipType === 'horizontal') {
          scaleX = -1
        } else if (flipType === 'vertical') {
          scaleY = -1
        } else if (flipType === 'both') {
          scaleX = -1
          scaleY = -1
        }

        imageStyle.value = `
        transform: rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY});
        -webkit-transform: rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY});
        -moz-transform: rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY});
        -o-transform: rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY});
        -ms-transform: rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY});
      `
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    if (props.image) {
      imageURL.value = URL.createObjectURL(props.image)
    }
  })

  function handImageLoad(event) {
    imageDimensions.value.width = event.target.naturalWidth
    imageDimensions.value.height = event.target.naturalHeight
    imageSize.value = formatBytes(props.image.size)

    // Add image data
    featureStore.imageData.push({
      name: props.image.name,
      width: imageDimensions.value.width,
      height: imageDimensions.value.height,
    })
  }

  function removeImage() {
    // Delete image data
    featureStore.imageData = featureStore.imageData.filter((e) => e.name !== props.image.name)

    featureStore.images = featureStore.images.filter((e) => e !== props.image)
    if (featureStore.images.length === 0) {
      router.replace({ query: { ...route.query, task: undefined, keep_uploaded: undefined } })
    }
  }
</script>
